import {
  type Control, Controller, type FieldPath, type FieldValues,
} from "react-hook-form";
import { Label } from "flowbite-react";
import { P } from "@/shared/ui/typographies/paragraph";
import { cn } from "@/shared/utils/style.util";
import type { LucideIcon } from "lucide-react";

type IconControlledInputProps<T extends FieldValues> = {
  id: string
  label: string
  name: FieldPath<T>
  control: Control<T>
  icon: LucideIcon
  type?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
};

export const IconControlledInput = <T extends FieldValues>({
  id,
  label,
  name,
  control,
  icon: Icon,
  type = "text",
  placeholder,
  required,
  disabled,
}: IconControlledInputProps<T>) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState: { error } }) => (
      <div className="w-full">
        <Label htmlFor={id} color={error ? "failure" : "default"}>
          {label}
          {required && " *"}
        </Label>
        <div className="relative mt-1">
          <Icon
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
            aria-hidden
          />
          <input
            {...field}
            id={id}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              "h-11 w-full rounded-lg border bg-transparent pl-10 pr-3 text-sm",
              "text-gray-900 placeholder:text-gray-400 dark:text-white dark:placeholder:text-gray-500",
              "focus:outline-none focus:ring-2 focus:ring-primary/10",
              "disabled:cursor-not-allowed disabled:opacity-50",
              error
                ? "border-red-500 focus:border-red-500 dark:border-red-500"
                : "border-gray-300 focus:border-primary dark:border-gray-600",
            )}
          />
        </div>
        {error && (
          <P className="mt-1 text-sm" color="error">
            {error.message}
          </P>
        )}
      </div>
    )}
  />
);

IconControlledInput.displayName = "IconControlledInput";
